import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { ConfirmProvider } from "./components/ConfirmProvider";

// Pages
import LoginPage from "./pages/LoginPage";
import WorkerDashboard from "./pages/WorkerDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import SupervisorDashboard from "./pages/SupervisorDashboard";
import ReportWriter from "./pages/ReportWriter";
import ReportsPage from "./pages/ReportsPage";
import ClientsPage from "./pages/ClientsPage";
import WellbeingHub from "./pages/WellbeingHub";
import HandoverPage from "./pages/HandoverPage";
import CompliancePage from "./pages/CompliancePage";
import SavedPdfsPage from "./pages/SavedPdfsPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TemplateEditorPage from "./pages/TemplateEditorPage";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import TeamChat from "./pages/TeamChat";
import NdisExportPage from "./pages/NdisExportPage";
import ReportBuilderPage from "./pages/ReportBuilderPage";
import ParticipantDashboard from "./pages/ParticipantDashboard";
import BehaviourTrackingPage from "./pages/BehaviourTrackingPage";
import RosterPage from "./pages/RosterPage";
import TeamAllocationsPage from "./pages/TeamAllocationsPage";
import TimesheetsPage from "./pages/TimesheetsPage";
import PayrollPage from "./pages/PayrollPage";
import TrainingPage from "./pages/TrainingPage";
import OrgSignupPage from "./pages/OrgSignupPage";
import OrgSettingsPage from "./pages/OrgSettingsPage";
import { OfflineIndicator } from "./components/OfflineIndicator";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const dest = user.role === 'organisation' ? '/admin' : user.role === 'supervisor' ? '/supervisor' : user.role === 'participant' ? '/participant' : '/dashboard';
    return <Navigate to={dest} replace />;
  }

  return children;
};

// Public Route - Redirect to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (user) {
    const dest = user.role === 'organisation' ? '/admin' : user.role === 'supervisor' ? '/supervisor' : user.role === 'participant' ? '/participant' : '/dashboard';
    return <Navigate to={dest} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/signup-org" element={<OrgSignupPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Worker Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><WorkerDashboard /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/reports/new" element={<ProtectedRoute><ReportWriter /></ProtectedRoute>} />
      <Route path="/reports/:id" element={<ProtectedRoute><ReportWriter /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/wellbeing" element={<ProtectedRoute><WellbeingHub /></ProtectedRoute>} />
      <Route path="/handover" element={<ProtectedRoute><HandoverPage /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/saved-pdfs" element={<ProtectedRoute><SavedPdfsPage /></ProtectedRoute>} />
      
      {/* Supervisor Routes */}
      <Route path="/supervisor" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><SupervisorDashboard /></ProtectedRoute>} />
      
      {/* Participant Routes */}
      <Route path="/participant" element={<ProtectedRoute allowedRoles={['participant']}><ParticipantDashboard /></ProtectedRoute>} />
      <Route path="/behaviour-tracking" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><BehaviourTrackingPage /></ProtectedRoute>} />
      
      {/* Organisation Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['organisation']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['organisation']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/templates" element={<ProtectedRoute allowedRoles={['organisation']}><TemplateEditorPage /></ProtectedRoute>} />
      <Route path="/settings/organisation" element={<ProtectedRoute><OrgSettingsPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/audit-trail" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/team-chat" element={<ProtectedRoute><TeamChat /></ProtectedRoute>} />
      <Route path="/roster" element={<ProtectedRoute><RosterPage /></ProtectedRoute>} />
      <Route path="/team-allocations" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><TeamAllocationsPage /></ProtectedRoute>} />
      <Route path="/timesheets" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><TimesheetsPage /></ProtectedRoute>} />
      <Route path="/payroll" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><PayrollPage /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><TrainingPage /></ProtectedRoute>} />
      <Route path="/ndis-export" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><NdisExportPage /></ProtectedRoute>} />
      <Route path="/report-builder" element={<ProtectedRoute allowedRoles={['supervisor', 'organisation']}><ReportBuilderPage /></ProtectedRoute>} />
      
      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  // Register service worker for offline support
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ConfirmProvider>
          <AppRoutes />
          <OfflineIndicator />
          <Toaster position="top-right" richColors />
        </ConfirmProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
